import * as React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router';
import { Box } from '@mui/material';
import { AnyAction } from '@reduxjs/toolkit';
import { IconProp } from '@fortawesome/fontawesome-svg-core';

import {
    ContextActionType,
    EmptyBox,
    HeaderActionButton,
    HeaderListSkeleton,
    ListHeader,
    ListSkeleton,
    SkeletonLoader,
    useDumbActionsContext,
} from '@europrocurement/flexy-components';
import {
    DossierOpenSearch,
    DossierOpenSearchSelectorMain,
    DOSSIERS_OPEN_SEARCH_SLICE_NAME,
    useFormaliteService,
    useSociete,
} from '@europrocurement/l2d-domain';
import {
    DossierActionContext,
    dossiersBasePath,
    DossierTable,
    parcoursFormalitePathResolver,
    PossibleTypes,
    useDefaultContextActions,
    useSearchBar,
} from '@europrocurement/l2d-modules';
import { DataSource } from '@europrocurement/l2d-redux-utils';
import { createFormalityFolioIcon, FaOptionIcon } from '@europrocurement/l2d-icons';

import { useClickActions, useInitialLoadingState } from '@europrocurement/l2d-hooks';
import {
    REALM_FORMALIST,
    REALM_RESP_FORMALIST,
    UseKeycloakService,
} from '@europrocurement/l2d-keycloak';
import { useDossierTable } from '../../hooks';

/**
 * Page de listing des dossiers de formalités.
 */
const ListeDossiers: React.FunctionComponent = function () {
    const navigate = useNavigate();
    const dispatch = useDispatch();

    const datasource: DataSource<DossierOpenSearch> = useSelector(DossierOpenSearchSelectorMain);
    const { isInitialLoading, isInError } = useInitialLoadingState(datasource);
    const { setIsSearchBarPath, setSearchBarType } = useSearchBar();

    const xIdSociete = useSociete();
    const { getOpenSearchDossiers } = useFormaliteService();
    const { updatingDossier } = React.useContext(DossierActionContext);
    const { backContextActions } = useDefaultContextActions();
    const { navigateOnClick } = useClickActions({ navigate });

    const { columns, filters, preFilters, rowActions } = useDossierTable();
    const [filtersInitialized, setFiltersInitialized] = React.useState<boolean>(false);

    const breadcrumbPath = React.useMemo(
        () => [
            {
                link: '/accueil',
                icon: {
                    icon: ['fasl', 'house'] as IconProp,
                    sx: { display: 'flex', alignItems: 'center' },
                },
            },
            { link: '/dossiers', path: 'Formalités' },
            'Liste des dossiers de formalité',
        ],
        [],
    );

    // Activation du flag de recherche de dossiers de formalité
    React.useEffect(() => {
        setSearchBarType(PossibleTypes.FormalityFolio);
        setIsSearchBarPath(true);
        return () => {
            setSearchBarType(undefined);
            setIsSearchBarPath(false);
        };
    }, [setIsSearchBarPath, setSearchBarType]);

    const keycloakService = UseKeycloakService();

    const isFormalist = keycloakService.checkRole(REALM_FORMALIST);
    const isRespFormalist = keycloakService.checkRole(REALM_RESP_FORMALIST);
    const { idUserGie } = keycloakService.getTokenParsed();

    const filtersClear = React.useCallback(() => {
        if (filters) {
            filters
                .map((item) => item.field)
                .forEach((f: string) => {
                    dispatch({
                        type: `${DOSSIERS_OPEN_SEARCH_SLICE_NAME}/deletemainFilter`,
                        payload: { field: f },
                    });
                });
        }

        dispatch(getOpenSearchDossiers({}) as unknown as AnyAction);
    }, [filters, dispatch, getOpenSearchDossiers]);

    // Clear filters on dismount
    React.useEffect(
        () => filtersClear(),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [],
    );

    // Initialisation des filtres et chargement des données
    React.useEffect(() => {
        dispatch({
            type: `${DOSSIERS_OPEN_SEARCH_SLICE_NAME}/setmainPagination`,
            payload: { page: 1, itemsPerPage: 10 },
        });
        dispatch({
            type: `${DOSSIERS_OPEN_SEARCH_SLICE_NAME}/setmainFilter`,
            payload: { key: 'societeId', value: [xIdSociete] },
        });

        if ((isFormalist || isRespFormalist) && idUserGie !== undefined && idUserGie !== null) {
            dispatch({
                type: `${DOSSIERS_OPEN_SEARCH_SLICE_NAME}/setmainFilter`,
                payload: { key: 'formalisteId', value: [idUserGie] },
            });
            dispatch({
                type: `${DOSSIERS_OPEN_SEARCH_SLICE_NAME}/setmainFilterLabel`,
                payload: { key: 'formalisteId', label: keycloakService.getName() },
            });
            dispatch(getOpenSearchDossiers({}) as unknown as AnyAction);
        }
        dispatch(getOpenSearchDossiers({}) as unknown as AnyAction);
        setFiltersInitialized(true);
    }, [
        dispatch,
        getOpenSearchDossiers,
        xIdSociete,
        updatingDossier,
        isFormalist,
        isRespFormalist,
        idUserGie,
        keycloakService,
    ]);

    const handleRowClick = React.useCallback(
        ({ id }: DossierOpenSearch) => {
            navigateOnClick({ path: `/${dossiersBasePath}/${id}` });
        },
        [navigateOnClick],
    );

    const handleRowWheelClick = React.useCallback(
        ({ id }: DossierOpenSearch) => {
            navigateOnClick({ path: `/${dossiersBasePath}/${id}`, openNewTab: true });
        },
        [navigateOnClick],
    );

    const handleCreateDossier = React.useCallback(() => {
        navigate(parcoursFormalitePathResolver('stepPrescriber'));

        // navigate(parcoursFormalitePathResolver('stepFormalite'));
    }, [navigate]);

    const contextActions: ContextActionType[] = React.useMemo(
        () => [
            {
                name: 'Nouveau dossier',
                icon: <FaOptionIcon {...createFormalityFolioIcon.props} />,
                action: handleCreateDossier,
            },
            ...backContextActions,
        ],
        [backContextActions, handleCreateDossier],
    );

    useDumbActionsContext(contextActions);

    const errorContent = React.useMemo(
        () => (
            <Box
                sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    height: '100vh',
                    width: '100%',
                }}
            >
                <EmptyBox
                    sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'space-evenly',
                        height: '25%',
                        width: '50%',
                    }}
                    text="Une erreur s'est produite lors de la récupération des dossiers."
                    subText="Veuillez recharger la page. Si le problème persiste, contactez votre administrateur."
                />
            </Box>
        ),
        [],
    );

    // Attendre l'initialisation des filtres avant d'afficher quoi que ce soit
    if (!filtersInitialized) {
        return <ListSkeleton columns={6} />;
    }

    return (
        <SkeletonLoader
            isLoading={isInitialLoading}
            skeleton={
                <>
                    <HeaderListSkeleton />
                    <ListSkeleton columns={6} />
                </>
            }
            isInError={isInError}
            errorChildren={errorContent}
        >
            <ListHeader
                breadcrumbPath={breadcrumbPath}
                actions={
                    <HeaderActionButton
                        label="Créer un nouveau dossier de formalité"
                        icon={createFormalityFolioIcon}
                        onClick={handleCreateDossier}
                    />
                }
            />

            {datasource && (
                <DossierTable
                    datasource={datasource}
                    columns={columns}
                    filters={filters}
                    preFilters={preFilters}
                    rowActions={rowActions}
                    onRowClick={handleRowClick}
                    onWheelClick={handleRowWheelClick}
                />
            )}
        </SkeletonLoader>
    );
};

export default ListeDossiers;
